import { useQuery } from "@tanstack/react-query";
import {
  Bike,
  CircleDollarSign,
  RefreshCw,
  ShoppingBag,
  Store,
  Users,
  Wallet,
  Activity,
} from "lucide-react";
import { Button } from "@veorest/ui";
import { fetchAdminOverview } from "@veorest/api-client";
import { StatsStrip } from "../components/data/StatsStrip";
import { AdminChartCard } from "../components/data/AdminChartCard";
import { EmptyState } from "../components/data/EmptyState";
import { PageHeader } from "../components/shell/PageHeader";
import { formatIqd, formatNumber, formatDate } from "../lib/format";

export default function OverviewPage() {
  const overview = useQuery({
    queryKey: ["admin-overview"],
    queryFn: fetchAdminOverview,
    refetchInterval: 60_000,
  });

  const stats = overview.data?.stats ?? {};
  const tiles = [
    {
      key: "restaurants",
      label: "Clients",
      value: formatNumber(stats.total_restaurants),
      icon: Store,
      tone: "accent" as const,
      caption: `${formatNumber(stats.active_restaurants)} active`,
    },
    {
      key: "orders",
      label: "Total orders",
      value: formatNumber(stats.total_orders),
      icon: ShoppingBag,
      tone: "info" as const,
      caption: "Platform-wide billable",
    },
    {
      key: "revenue",
      label: "Credit revenue",
      value: formatIqd(stats.credit_revenue_iqd),
      icon: CircleDollarSign,
      tone: "success" as const,
      caption: "Paid package purchases",
    },
    {
      key: "customers",
      label: "Global customers",
      value: formatNumber(stats.total_customers_global),
      icon: Users,
      tone: "ink" as const,
      caption: `${formatNumber(stats.total_customers_restaurant)} client CRM`,
    },
    {
      key: "drivers",
      label: "Drivers online",
      value: `${formatNumber(stats.active_drivers)} / ${formatNumber(stats.total_drivers)}`,
      icon: Bike,
      tone: "info" as const,
      caption: "Live presence",
    },
    {
      key: "consumed",
      label: "Credits consumed",
      value: formatIqd(stats.credits_consumed_iqd),
      icon: Wallet,
      tone: "accent" as const,
      caption: "Order deductions",
    },
    {
      key: "balance",
      label: "Open credit balance",
      value: formatIqd(stats.total_credit_balance_iqd),
      icon: Wallet,
      tone: "success" as const,
      caption: "Across all clients",
    },
    {
      key: "suspended",
      label: "Suspended",
      value: formatNumber(stats.suspended_restaurants),
      icon: Store,
      tone: "ink" as const,
      caption: `${formatNumber(stats.trial_restaurants)} on trial`,
    },
  ];

  const ordersChart =
    overview.data?.charts.ordersOverTime.map((d) => ({
      label: d.day.slice(5),
      value: d.orders,
    })) ?? [];
  const revenueChart =
    overview.data?.charts.revenueOverTime.map((d) => ({
      label: d.day.slice(5),
      value: d.revenue,
    })) ?? [];
  const topChart =
    overview.data?.topRestaurants.map((r) => ({
      label: r.name.length > 12 ? `${r.name.slice(0, 11)}…` : r.name,
      value: r.orderCount,
    })) ?? [];

  return (
    <div className="menu-page overview-page">
      <PageHeader
        compact
        eyebrow="Platform"
        title="Overview"
        subtitle="Live OELOX ecosystem health, credits, and growth."
        actions={
          <Button
            variant="secondary"
            className="min-h-[40px]"
            icon={<RefreshCw className="h-4 w-4" strokeWidth={2.25} />}
            onClick={() => void overview.refetch()}
          >
            Refresh
          </Button>
        }
      />

      {overview.isError ? (
        <div className="overview-page__error" role="alert">
          <p>{(overview.error as Error).message}</p>
          <button
            type="button"
            className="overview-page__retry"
            onClick={() => void overview.refetch()}
          >
            <RefreshCw className="h-4 w-4" strokeWidth={2.25} />
            Retry
          </button>
        </div>
      ) : null}

      <StatsStrip tiles={tiles} loading={overview.isLoading && !overview.data} />

      <div className="overview-page__grid mt-1">
        <AdminChartCard
          title="Orders over time"
          caption="Last 30 days · billable orders"
          empty="No order activity yet"
          data={ordersChart}
          accentColor="var(--accent)"
          gradientId="adminOrdersFill"
          formatValue={(v) => formatNumber(v)}
        />
        <AdminChartCard
          title="Revenue over time"
          caption="Paid credit purchases (IQD)"
          empty="No revenue recorded yet"
          data={revenueChart}
          accentColor="var(--success)"
          gradientId="adminRevFill"
          formatValue={(v) => formatIqd(v)}
        />
      </div>

      <div className="overview-page__grid mt-4">
        <AdminChartCard
          title="Top clients"
          caption="Orders in the last 30 days"
          empty="No clients ranked yet"
          data={topChart}
          accentColor="var(--accent)"
          gradientId="adminTopFill"
          formatValue={(v) => formatNumber(v)}
          variant="bar"
        />

        <section className="overview-panel">
          <header className="overview-panel__header">
            <span className="overview-panel__icon" aria-hidden>
              <Activity className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="overview-panel__title">Recent activity</h3>
              <p className="overview-panel__subtitle">Admin actions across the platform</p>
            </div>
          </header>
          <div className="overview-panel__body">
            {(overview.data?.recentActivity ?? []).length === 0 ? (
              <EmptyState
                icon={<Activity className="h-5 w-5" />}
                title="No activity yet"
                body="Credits, payments, and client actions will show up here."
              />
            ) : (
              <div className="admin-activity-list">
                {overview.data?.recentActivity.map((a) => (
                  <div key={a.id} className="admin-activity-row">
                    <div className="min-w-0">
                      <p className="admin-activity-row__title">{a.summary}</p>
                      <p className="admin-activity-row__meta">
                        {a.actor_label || "System"} · {a.action}
                      </p>
                    </div>
                    <span className="admin-activity-row__time">{formatDate(a.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
